import { useEffect, useRef, useState } from 'react';
import {
  admitStudent, closeSession, listenToSession, openSession, passTurn, touchSession,
  type LiveSession,
} from '../firebase/sessions';
import { listenToQueue } from '../firebase/queue';
import { activeStudents, type QueuedStudent } from './queue';
import { sessionId } from './sessionKey';
import { MUQRI_BEAT_MS } from './presence';
import { markedAyah } from './marker';
import { nextReciter, type SessionMode } from './turns';

export type MuqriFailure = 'open' | 'close' | 'admit' | 'passTurn';

export type MuqriSession = {
  /** Today's session id once it is open, null while it is not. */
  sessionIdValue: string | null;
  isOpen: boolean;
  mode: SessionMode;
  setMode: (m: SessionMode) => void;
  /** Everyone still beating in the queue, in join order. */
  present: QueuedStudent[];
  /** Everyone the SERVER admitted — not the same question as the queue. */
  participants: string[];
  currentReciterId: string | null;
  session: LiveSession | null;
  /** The last action that was refused, so the screen can name it. */
  failure: MuqriFailure | null;
  open: () => Promise<void>;
  close: () => Promise<void>;
  admit: (studentId: string, onAdmitted: (room: string) => void) => Promise<void>;
  /**
   * Passes the turn on. With no argument it takes the next in the queue; with
   * a uid it calls THAT pupil — م-4-03's "يستدعي المعلم طالباً للتسميع"،
   * which is the lab's whole interaction.
   */
  handOver: (studentId?: string) => Promise<void>;
};

/**
 * Running a session, minus how it draws. Shared by both clients.
 *
 * The four rules that cost the most to learn:
 *
 * - The session is RECOVERED on mount, from an id derived as
 *   `${muqriId}-${YYYY-MM-DD}` rather than remembered. Local state survived
 *   only while the screen stayed mounted, so a reload put the toggle back to
 *   "closed" while the session was still open in Firestore — and the queue
 *   went with it, leaving students waiting in a session nobody could see.
 * - The beat says the معلم is still here. An open session used to advertise
 *   itself whether or not anyone was watching, so students queued into a room
 *   nobody was attending. A missed beat only unlists; it never closes.
 * - Who is IN THE ROOM is not who is in the queue. passTurn validates the
 *   next reciter against activeCall.participants, so naming someone from the
 *   queue is refused with not-a-participant — every time, in the only mode
 *   that has a turn to pass.
 * - Nothing here writes currentReciterId or the room. Those are server-owned;
 *   a local copy is a second, wrong answer to a question only the server may
 *   answer.
 */
export function useMuqriSession(params: {
  muqriId: string;
  muqriName: string;
  /**
   * The languages this معلم teaches in — م-7-02, copied onto the session when
   * it opens so pupils can filter without reading the teacher's own document,
   * which the rules do not allow them.
   */
  languages?: unknown;
  /** The معلم's own gender — sessions are same-gender. */
  gender?: unknown;
}): MuqriSession {
  const { muqriId, muqriName, languages, gender } = params;
  const [mode, setMode] = useState<SessionMode>('private');
  const [sessionIdValue, setSessionIdValue] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueuedStudent[]>([]);
  const [session, setSession] = useState<LiveSession | null>(null);
  const [failure, setFailure] = useState<MuqriFailure | null>(null);
  /** True from the moment an open is requested until the document confirms it. */
  const openingRef = useRef(false);

  useEffect(() => {
    const todays = sessionId(muqriId, new Date());
    return listenToSession(todays, (live) => {
      if (live && live.status === 'open') {
        setSessionIdValue(todays);
        setMode(live.mode);
      } else if (!openingRef.current) {
        // Not cleared while an open is in flight: openSession awaits its own
        // write, but the listener can still deliver one last "no document"
        // frame in between, which would put the toggle back to closed a beat
        // after the معلم moved it.
        setSessionIdValue(null);
      }
    });
  }, [muqriId]);

  useEffect(() => {
    if (!sessionIdValue) return undefined;
    const beat = () => {
      void touchSession(sessionIdValue).catch(() => {
        // A missed beat is not fatal: the next one relists the session, and
        // until then it is merely unlisted, not closed.
      });
    };
    // Immediately as well as on the interval, so coming back to this screen
    // relists the session at once rather than after the first tick.
    beat();
    const timer = setInterval(beat, MUQRI_BEAT_MS);
    return () => clearInterval(timer);
  }, [sessionIdValue]);

  useEffect(() => {
    if (!sessionIdValue) return undefined;
    return listenToQueue(sessionIdValue, setQueue);
  }, [sessionIdValue]);

  useEffect(() => {
    if (!sessionIdValue) { setSession(null); return undefined; }
    return listenToSession(sessionIdValue, setSession);
  }, [sessionIdValue]);

  const participants = session?.activeCall?.participants ?? [];
  const currentReciterId = session?.currentReciterId ?? null;
  const present = activeStudents(queue, Date.now());

  const open = async () => {
    setFailure(null);
    openingRef.current = true;
    try {
      setSessionIdValue(await openSession(muqriId, muqriName, mode, languages, gender));
    } catch {
      // A refusal here (the rules rejecting the name, a squatted id) once
      // escaped as an unhandled rejection: nothing rendered and the معلم was
      // left staring at the open screen with no feedback.
      setFailure('open');
    } finally {
      openingRef.current = false;
    }
  };

  const close = async () => {
    if (!sessionIdValue) return;
    setFailure(null);
    try {
      // The marker rides along so the server records where the student who
      // still held the turn got to.
      await closeSession(sessionIdValue, markedAyah(sessionIdValue));
    } catch {
      // Dropping the local state on a close that never landed would tell the
      // معلم their session is shut while it is still open and advertising.
      setFailure('close');
      return;
    }
    setSessionIdValue(null);
    setQueue([]);
    setSession(null);
  };

  const admit = async (studentId: string, onAdmitted: (room: string) => void) => {
    if (!sessionIdValue) return;
    setFailure(null);
    try {
      const room = await admitStudent(sessionIdValue, studentId, muqriId);
      onAdmitted(room);
    } catch {
      // A refusal must be visible rather than a navigation that silently
      // never happens.
      setFailure('admit');
    }
  };

  const handOver = async (studentId?: string) => {
    if (!sessionIdValue) return;
    // nextReciter only picks a candidate, on this device, from the queue it is
    // already watching. passTurn is the server callable that authorises the
    // move and revokes the outgoing reciter's microphone — and it is what
    // refuses a pupil the معلم may not call, named or not.
    const next = studentId ?? nextReciter({ mode, muqriId, participants, currentReciterId }, present);
    setFailure(null);
    try {
      await passTurn(sessionIdValue, next, markedAyah(sessionIdValue));
    } catch {
      setFailure('passTurn');
    }
  };

  return {
    sessionIdValue,
    isOpen: !!sessionIdValue,
    mode,
    setMode,
    present,
    participants,
    currentReciterId,
    session,
    failure,
    open,
    close,
    admit,
    handOver,
  };
}
